import React, { useMemo, useState } from "react";
import DataList from "components/datatable/DataList";
import PopupDialog from "components/dialog/PopupDialog";
import { showEmpty } from "features/kms/system/SystemPageUtils";

function formatDateInput(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

function defaultPeriod() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6);
  return {
    tanggal_mulai: formatDateInput(start),
    tanggal_sampai: formatDateInput(end),
  };
}

function formatLocaleDateTime(value) {
  if (!value) return "-";
  const date = parseApiDateTime(value);
  if (Number.isNaN(date.getTime())) return showEmpty(value);
  const datePart = [
    `${date.getDate()}`.padStart(2, "0"),
    `${date.getMonth() + 1}`.padStart(2, "0"),
    date.getFullYear(),
  ].join("/");
  const timePart = [
    `${date.getHours()}`.padStart(2, "0"),
    `${date.getMinutes()}`.padStart(2, "0"),
    `${date.getSeconds()}`.padStart(2, "0"),
  ].join(":");
  return `${datePart} ${timePart}`;
}

function parseApiDateTime(value) {
  if (!value) return new Date("");
  const normalizedValue = String(value).replace(/([+-]\d{2})$/, "$1:00");
  return new Date(normalizedValue);
}

function parsePeriodStart(value) {
  const date = new Date(`${value || ""}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parsePeriodEnd(value) {
  const date = new Date(`${value || ""}T23:59:59.999`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isDateInPeriod(value, period) {
  const date = parseApiDateTime(value);
  const start = parsePeriodStart(period?.tanggal_mulai);
  const end = parsePeriodEnd(period?.tanggal_sampai);
  if (Number.isNaN(date.getTime()) || !start || !end) return false;
  return date >= start && date <= end;
}

function resolveStatusAktivitas(item, period) {
  const totalAktivitas = Number(item?.jml_aktivitas || 0);
  const loginInPeriod = isDateInPeriod(item?.last_login_at, period);
  if (loginInPeriod && totalAktivitas > 0) return "Aktif";
  if (loginInPeriod && totalAktivitas <= 0) return "Perlu Perhatian";
  if (totalAktivitas <= 0 && !loginInPeriod) return "Tidak Aktif";
  return "Aktif";
}

function StatusBadge({ value }) {
  const status = String(value || "");
  const cls =
    status === "Aktif"
      ? "badge badge-success"
      : status === "Perlu Perhatian"
      ? "badge badge-warning"
      : status === "Tidak Aktif"
      ? "badge badge-secondary"
      : "badge badge-light";
  return <span className={cls}>{showEmpty(status)}</span>;
}

export default function CaborMonitoringAktifitasPage() {
  const initialPeriod = useMemo(() => defaultPeriod(), []);
  const [period, setPeriod] = useState(initialPeriod);
  const [activityPopup, setActivityPopup] = useState(null);

  const openActivity = (item) => {
    if (!item?.id_organisasi) return;
    setActivityPopup({
      id_organisasi: item.id_organisasi,
      title: "Detail Aktivitas",
      id_audit_log: "",
      induk_organisasi_name: item.induk_organisasi_name,
    });
  };

  const numberLink = (value, onClick, disabled = false) => {
    const text = showEmpty(value, "0");
    if (disabled || `${text}` === "0") return text;
    return (
      <a
        href="/"
        onClick={(event) => {
          event.preventDefault();
          onClick();
        }}
      >
        {text}
      </a>
    );
  };

  const coltype = {
    no: { label: "No.", width: 48, align: "center", pdfwidth: 5 },
    id_monitoring_aktifitas: { type: "hidden", name: "id_monitoring_aktifitas" },
    id_organisasi: { type: "hidden", name: "id_organisasi" },
    induk_organisasi_name: {
      label: "Induk Organisasi",
      renderFunc: (val) => showEmpty(val),
      width: 240,
      pdfwidth: 30,
    },
    cabor_name: {
      label: "Cabor",
      renderFunc: (val) => showEmpty(val),
      width: 280,
      pdfwidth: 38,
    },
    jml_sdm: {
      label: "Jml SDM & Pengurus Unik",
      renderFunc: (val) => showEmpty(val, "0"),
      width: 150,
      align: "center",
      pdfwidth: 18,
    },
    jml_aktivitas: {
      label: "Jml Aktifitas",
      renderFunc: (val, col, item) => numberLink(val, () => openActivity(item)),
      renderExportFunc: (val) => showEmpty(val, "0"),
      width: 95,
      align: "center",
      pdfwidth: 12,
    },
    last_login_at: {
      label: "Terakhir Login",
      renderFunc: (val) => formatLocaleDateTime(val),
      renderExportFunc: (val) => formatLocaleDateTime(val),
      width: 150,
      align: "center",
      pdfwidth: 20,
    },
    status_aktivitas: {
      label: "Status Aktivitas",
      renderFunc: (val) => <StatusBadge value={val} />,
      renderExportFunc: (val) => showEmpty(val),
      width: 125,
      align: "center",
      pdfwidth: 16,
    },
  };

  const activityColtype = {
    no: { label: "No.", width: 48, align: "center" },
    id_audit_log: { label: "ID", width: 70, align: "center" },
    created_at: {
      label: "Waktu",
      renderFunc: (val) => formatLocaleDateTime(val),
      renderExportFunc: (val) => formatLocaleDateTime(val),
      width: 100,
      align: "center",
    },
    username: { label: "Username", renderFunc: (val) => showEmpty(val), width: 150 },
    audit_action_label: {
      label: "Aktifitas",
      renderFunc: (val) => showEmpty(val),
      width: 145,
    },
    entity_label: {
      label: "Objek",
      renderFunc: (val) => showEmpty(val),
      width: 150,
    },
    entity_id: { label: "Entity ID", renderFunc: (val) => showEmpty(val), width: 220 },
    ringkasan: { label: "Ringkasan", renderFunc: (val) => showEmpty(val), width: 280 },
    ip_address: { label: "IP Address", renderFunc: (val) => showEmpty(val), width: 140 },
    user_agent: { label: "User Agent", renderFunc: (val) => showEmpty(val), width: 260 },
  };

  return (
    <>
      <DataList
        title="Monitoring Aktifitas"
        coltype={coltype}
        dlist={{
          url: "/data-cabor/monitoring-aktifitas",
          adapter: (resp) => ({
            ...resp,
            data: (resp?.data || []).map((item) => ({
              ...item,
              status_aktivitas: resolveStatusAktivitas(item, period),
            })),
          }),
          columns: [
            "no",
            "induk_organisasi_name",
            "cabor_name",
            "jml_sdm",
            "jml_aktivitas",
            "last_login_at",
            "status_aktivitas",
          ],
          filter: [
            { dtype: "q", name: "q", colsize: 3 },
            {
              dtype: "date",
              name: "tanggal_mulai",
              label: "Tanggal Mulai",
              colsize: 2,
              value: initialPeriod.tanggal_mulai,
            },
            {
              dtype: "date",
              name: "tanggal_sampai",
              label: "Sampai Tanggal",
              colsize: 2,
              value: initialPeriod.tanggal_sampai,
            },
          ],
          sort_by: [
            "induk_organisasi_name",
            "cabor_name",
            "jml_sdm",
            "jml_aktivitas",
            "last_login_at",
            "status_aktivitas",
          ],
          onFilterChange: (nextKeyword) =>
            setPeriod({
              tanggal_mulai:
                nextKeyword?.tanggal_mulai || initialPeriod.tanggal_mulai,
              tanggal_sampai:
                nextKeyword?.tanggal_sampai || initialPeriod.tanggal_sampai,
            }),
        }}
        detailconfig={{
          url: "/data-cabor/monitoring-aktifitas",
          cellid: "id_organisasi",
          title: "Detail Monitoring",
          tooltip: "Detail",
          icon: "fa-search",
          size: "xl",
          columns: [
            "induk_organisasi_name",
            "cabor_name",
            "jml_sdm",
            "jml_aktivitas",
            "last_login_at",
            "status_aktivitas",
          ],
        }}
        pdflist={{
          title: "Monitoring Aktifitas",
          columns: [
            "no",
            "induk_organisasi_name",
            "cabor_name",
            "jml_sdm",
            "jml_aktivitas",
            "last_login_at",
            "status_aktivitas",
          ],
        }}
        xlslist={{
          title: "Monitoring Aktifitas",
          columns: [
            "no",
            "induk_organisasi_name",
            "cabor_name",
            "jml_sdm",
            "jml_aktivitas",
            "last_login_at",
            "status_aktivitas",
          ],
        }}
        pdfconfig={{ orientation: "landscape" }}
      />

      <PopupDialog
        size="xl"
        title={`${activityPopup?.title || "Detail Aktivitas"}${
          activityPopup?.induk_organisasi_name
            ? ` - ${activityPopup.induk_organisasi_name}`
            : ""
        }`}
        show={Boolean(activityPopup)}
        closeHandle={() => setActivityPopup(null)}
      >
        {activityPopup && (
          <DataList
            title=""
            coltype={activityColtype}
            dlist={{
              url: `/data-cabor/monitoring-aktifitas/${activityPopup.id_organisasi}/aktivitas`,
              columns: [
                "no",
                "created_at",
                "username",
                "audit_action_label",
                "entity_label",
                "ringkasan",
              ],
              filter: [
                {
                  dtype: "hidden",
                  name: "tanggal_mulai",
                  value: period.tanggal_mulai,
                },
                {
                  dtype: "hidden",
                  name: "tanggal_sampai",
                  value: period.tanggal_sampai,
                },
                ...(activityPopup.id_audit_log
                  ? [
                      {
                        dtype: "hidden",
                        name: "id_audit_log",
                        value: activityPopup.id_audit_log,
                      },
                    ]
                  : []),
                { dtype: "q", name: "q", colsize: 4 },
              ],
              sort_by: ["created_at", "username", "audit_action"],
            }}
            detailconfig={{
              url: `/data-cabor/monitoring-aktifitas/${activityPopup.id_organisasi}/aktivitas`,
              cellid: "id_audit_log",
              title: "Detail Log",
              size: "xl",
              columns: [
                "id_audit_log",
                "created_at",
                "username",
                "audit_action_label",
                "entity_label",
                "ringkasan",
                "ip_address",
                "user_agent",
              ],
            }}
          />
        )}
      </PopupDialog>

    </>
  );
}
